import React, { useState } from 'react';
import { Form, Input, Button, Col } from 'antd';
import { AiOutlineInfoCircle, AiOutlineArrowRight } from 'react-icons/ai';
import { HiOutlineLockClosed } from 'react-icons/hi';
import { BsPhone } from 'react-icons/bs';
import './MembershipLoginForm.less';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import useMembershipAuthHelpers from '../../../hooks/useMembershipAuthHelpers';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import ModelApiMessage, {
  ApiStatus,
  ApiStatusMessage,
} from '@skygroup/shared/components/crud-helpers/ModelApiMessage';
import { OtpMode } from '../../../store/config';

interface LoginFormProps {
  otpMode: OtpMode;
  setIsLoginSuccess: (success: boolean) => void;
  setSessionId: (sessionId: string) => void;
  onRegisterClick?: () => void;
}
const columnSize = {
  xs: 20,
  md: 35,
};

const MembershipLoginForm: React.FC<LoginFormProps> = ({
  otpMode,
  setIsLoginSuccess,
  setSessionId,
  onRegisterClick,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { translations: t, config } = useSelector(
    (data: RootState) => data.config
  );
  const { membershipLogin } = useMembershipAuthHelpers();

  const [apiStatusMessage, setApiStatusMessage] = useState(
    {} as ApiStatusMessage
  );

  const handleSubmit = async (values: { id?: string; phone: string }) => {
    if (!config.siteConfig?.membership?.auth_type) {
      console.error('Membership auth type not found');
      return;
    }

    setLoading(true);
    try {
      let response = await membershipLogin(
        values.id ?? '',
        values.phone,
        otpMode, // config.siteConfig?.membership?.auth_type
      );

      // Check if account is not registered
      if (response.data?.info?.is_registered === false) {
        setLoading(false);
        setApiStatusMessage({
          status: ApiStatus.warning,
          data: {
            title: t.auth.accountNotFound,
            message: (
              <Button
                style={{
                  padding: '2px',
                  margin: 0,
                  fontSize: '14px',
                  color: 'var(--primary-color)',
                }}
                type="text"
                onClick={onRegisterClick}
              >
                {t.auth.continueWithRegistration}
              </Button>
            ),
          },
        });
        return;
      }

      const sessionId = response.data?.session_id;
      if (!sessionId) {
        throw new Error('Session ID not found');
      }

      setSessionId(sessionId);
      setIsLoginSuccess(true);
    } catch (error: any) {
      return setApiStatusMessage({
        status: ApiStatus.error,
        data: { message: error.message ?? t.auth.loginError },
      });
    } finally {
      setLoading(false);
    }
  };

  const isMembershipMode = otpMode === OtpMode.MEMBERSHIP;

  return (
    <SpinLoader isLoading={loading} size="large" onTop={true}>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        className="membership-login-form"
      >
        {isMembershipMode && (
          <>
            <p className="login-description">{t.auth.loginDescription}</p>
            <p className="login-otp-info">{t.auth.otpInfo}</p>
          </>
        )}
        {otpMode === OtpMode.ORG_ISTADRUT && (
          <Form.Item
            label={t.auth.id}
            name="id"
            rules={[
              { required: true, message: t.auth.idPlaceholderError },
              { pattern: /^[0-9]{9}$/, message: t.auth.idDigits },
            ]}
          >
            <Input placeholder={t.auth.idPlaceholder} />
          </Form.Item>
        )}
        <Form.Item
          label={isMembershipMode ? t.auth.mobileNumber : t.auth.phone}
          name="phone"
          rules={[
            { required: true, message: t.auth.phonePlaceholderError },
            { pattern: /^[0-9]{9,}$/, message: t.auth.phoneDigits },
          ]}
        >
          <Input
            placeholder={t.auth.phonePlaceholder}
            type="text"
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              form.setFieldsValue({ phone: value });
            }}
            className="phone-input-numeric"
            prefix={
              isMembershipMode ? (
                <BsPhone className="phone-input-icon" />
              ) : undefined
            }
          />
        </Form.Item>
        {isMembershipMode && onRegisterClick && (
          <div className="registration-prompt">
            <span>{t.auth.newHere}</span>{' '}
            <button
              type="button"
              className="create-account-link"
              onClick={onRegisterClick}
            >
              {t.auth.createAccount}
            </button>
          </div>
        )}
        <Button className="btn-style-5" type="primary" htmlType="submit" block>
          {t.auth.sendCode}
          <AiOutlineArrowRight className="send-code-arrow" />
        </Button>
        {isMembershipMode && (
          <p className="privacy-notice">
            <HiOutlineLockClosed className="privacy-notice-icon" />
            {t.auth.privacyNotice}
          </p>
        )}
        <Col>
          <ModelApiMessage apiStatusMessage={apiStatusMessage} t={t.crud} />
        </Col>
      </Form>
    </SpinLoader>
  );
};

export default MembershipLoginForm;
